/**
 * The help assistant — "ask for help / get me back on track".
 *
 * WHAT THIS IS, AND WHAT IT IS DELIBERATELY NOT
 * It is a calm, DETERMINISTIC assistant, not a chatbot. There is no model, no
 * free-text box and no generated prose. Every word is a translated string and
 * every piece of advice is derived from the user's real records. It answers
 * three questions, in this order:
 *
 *   1. "What should I do next?"  — the SINGLE next best step, reused verbatim
 *      from the onboarding derivation (GET /onboarding → `nextStep`). We do not
 *      re-implement the state logic; we render what that endpoint already
 *      computes from the goal + the live compliance records.
 *   2. "Where am I?"             — a route-aware orientation for the current
 *      screen: one plain line plus a few "what you can do here" bullets and
 *      links to related screens. Static, translator-owned copy (helpStrings.ts).
 *   3. "What comes after that?"  — a mini roadmap: the next couple of steps
 *      after the current one, again straight from the onboarding checklist.
 *
 * REUSE
 * The next step, the roadmap, the progress figure and the goal/complete states
 * all come from `useOnboarding()` and are rendered with the onboarding string
 * table via `useStepText` — the same component the dashboard card and the
 * /getting-started page use. This file owns only the panel chrome and the
 * per-screen orientation.
 *
 * MOUNTING
 * `HelpButton` is the self-contained trigger (it owns the panel and its open
 * state). It is exported for the Shell to place; this file does not touch
 * Shell.tsx. `HelpPanel` is exported too, for anywhere that wants to drive the
 * panel itself.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';
import { useHelpT } from '../lib/helpStrings';
import { manualSlugForRoute } from '../lib/manual';
import {
  useOnboarding,
  useStepText,
  type OnboardingStep,
} from './Onboarding';
import { useOnboardingT } from '../lib/onboardingStrings';
import { Button, Pill, ProgressBar, Spinner, ErrorNotice, type Tone } from './ui';
import { IconHelp, IconChevron } from './icons';

// ---------------------------------------------------------------------------
//  The route-aware orientation model
// ---------------------------------------------------------------------------

/**
 * Per-screen help, matched against the current path in order — most specific
 * first, so `/assessments/:id` wins over `/assessments` and
 * `/vendors/assessments` over `/vendors`.
 *
 * `page` keys the copy in helpStrings.ts (`help.page.<page>.here`, `.p1`…).
 * `bullets` is how many `.pN` lines that page defines. `links` are related
 * screens; their labels reuse the existing `nav.*` keys so there is nothing new
 * to translate for them.
 */
interface RouteHelp {
  test: (path: string) => boolean;
  page: string;
  bullets: number;
  links: Array<{ to: string; labelKey: string }>;
}

const ROUTE_HELP: RouteHelp[] = [
  { test: (p) => p.startsWith('/assessments/'), page: 'assessmentDetail', bullets: 2, links: [{ to: '/gaps', labelKey: 'nav.gaps' }, { to: '/reports', labelKey: 'nav.reports' }] },
  { test: (p) => p === '/assessments', page: 'assessments', bullets: 2, links: [{ to: '/gaps', labelKey: 'nav.gaps' }, { to: '/frameworks', labelKey: 'nav.frameworks' }] },
  { test: (p) => p === '/vendors/assessments', page: 'vendorAssessments', bullets: 2, links: [{ to: '/vendors', labelKey: 'nav.vendors' }] },
  { test: (p) => p.startsWith('/vendors'), page: 'vendors', bullets: 2, links: [{ to: '/vendors/assessments', labelKey: 'nav.vendorAssessments' }] },
  { test: (p) => p.startsWith('/catalogue'), page: 'catalogue', bullets: 2, links: [{ to: '/frameworks', labelKey: 'nav.frameworks' }, { to: '/soa', labelKey: 'nav.soa' }] },
  { test: (p) => p.startsWith('/frameworks'), page: 'frameworks', bullets: 2, links: [{ to: '/assessments', labelKey: 'nav.assessments' }, { to: '/catalogue', labelKey: 'nav.catalogue' }] },
  { test: (p) => p === '/gaps', page: 'gaps', bullets: 2, links: [{ to: '/plans', labelKey: 'nav.plans' }, { to: '/assessments', labelKey: 'nav.assessments' }] },
  { test: (p) => p === '/plans', page: 'plans', bullets: 2, links: [{ to: '/gaps', labelKey: 'nav.gaps' }] },
  { test: (p) => p === '/getting-started', page: 'gettingStarted', bullets: 2, links: [{ to: '/', labelKey: 'nav.dashboard' }] },
  { test: (p) => p.startsWith('/incidents'), page: 'incidents', bullets: 2, links: [] },
  { test: (p) => p.startsWith('/policies'), page: 'policies', bullets: 2, links: [{ to: '/catalogue', labelKey: 'nav.catalogue' }] },
  { test: (p) => p === '/organizations', page: 'organizations', bullets: 2, links: [{ to: '/credits', labelKey: 'nav.credits' }, { to: '/users', labelKey: 'nav.users' }] },
  { test: (p) => p === '/users', page: 'users', bullets: 2, links: [{ to: '/security', labelKey: 'nav.security' }] },
  { test: (p) => p === '/reports', page: 'reports', bullets: 2, links: [{ to: '/soa', labelKey: 'nav.soa' }, { to: '/assessments', labelKey: 'nav.assessments' }] },
  { test: (p) => p === '/audit', page: 'audit', bullets: 1, links: [] },
  { test: (p) => p === '/security', page: 'security', bullets: 2, links: [] },
  { test: (p) => p === '/news', page: 'news', bullets: 1, links: [] },
  { test: (p) => p === '/translations', page: 'translations', bullets: 1, links: [] },
  { test: (p) => p === '/content-watch', page: 'contentWatch', bullets: 1, links: [{ to: '/catalogue', labelKey: 'nav.catalogue' }] },
  { test: (p) => p === '/soa', page: 'soa', bullets: 2, links: [{ to: '/reports', labelKey: 'nav.reports' }, { to: '/catalogue', labelKey: 'nav.catalogue' }] },
  { test: (p) => p === '/trust-centre', page: 'trustCentre', bullets: 2, links: [{ to: '/reports', labelKey: 'nav.reports' }] },
  { test: (p) => p === '/scans', page: 'scans', bullets: 2, links: [{ to: '/credits', labelKey: 'nav.credits' }, { to: '/vendors', labelKey: 'nav.vendors' }] },
  { test: (p) => p === '/credits', page: 'credits', bullets: 2, links: [{ to: '/organizations', labelKey: 'nav.organizations' }] },
  { test: (p) => p === '/', page: 'dashboard', bullets: 2, links: [{ to: '/getting-started', labelKey: 'nav.gettingStarted' }, { to: '/assessments', labelKey: 'nav.assessments' }] },
];

const GENERIC_HELP: RouteHelp = {
  test: () => true,
  page: 'generic',
  bullets: 1,
  links: [{ to: '/getting-started', labelKey: 'nav.gettingStarted' }],
};

function routeHelpFor(pathname: string): RouteHelp {
  return ROUTE_HELP.find((r) => r.test(pathname)) ?? GENERIC_HELP;
}

const STATE_TONE: Record<OnboardingStep['state'], Tone> = {
  DONE: 'good',
  PARTIAL: 'warning',
  TODO: 'neutral',
  BLOCKED: 'neutral',
};

// ---------------------------------------------------------------------------
//  The trigger — self-contained, owns the panel
// ---------------------------------------------------------------------------

/**
 * The persistent "Help / Guide me" entry point.
 *
 * Self-contained: it renders the trigger, owns the open state, and renders the
 * panel. The Shell only has to place this one element. `iconOnly` gives the
 * compact form for the mobile top bar; the default carries a label for the
 * desktop sidebar.
 */
export function HelpButton({
  iconOnly = false,
  className,
}: {
  iconOnly?: boolean;
  className?: string;
}) {
  const { hx } = useHelpT();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Return focus to the trigger when the panel closes, so a keyboard user is
  // put back exactly where they were rather than at the top of the document.
  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={hx('help.openAria')}
        title={hx('help.open')}
        className={cn(
          iconOnly
            ? 'p-2 rounded-lg text-ink-secondary hover:bg-surface-sunken'
            : 'oc-btn-ghost oc-btn-sm w-full',
          className,
        )}
      >
        <IconHelp className={iconOnly ? 'w-5 h-5' : 'w-4 h-4'} />
        {!iconOnly && <span>{hx('help.open')}</span>}
      </button>

      <HelpPanel open={open} onClose={close} />
    </>
  );
}

// ---------------------------------------------------------------------------
//  The panel
// ---------------------------------------------------------------------------

/**
 * The guidance panel.
 *
 * A quiet drawer: a bottom sheet on phones (thumb-reachable) and a right-hand
 * drawer from `sm` up. Escape closes it, the backdrop closes it, body scroll is
 * locked while open, and focus is trapped inside — Tab cycles within the panel
 * and never escapes to the page behind it.
 */
export function HelpPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { hx } = useHelpT();
  const { data, isLoading, isError, refetch } = useOnboarding();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape to close, body scroll lock, and a focus trap. Rebound whenever the
  // panel opens so the first focusable element is focused each time.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;

    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        panel?.focus();
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !panel?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the panel itself first; Tab then moves into its controls.
    panel?.focus();

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const route = useMemo(() => routeHelpFor(location.pathname), [location.pathname]);
  const manualSlug = useMemo(() => manualSlugForRoute(location.pathname), [location.pathname]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full sm:max-w-md sm:h-dvh max-h-[92dvh] sm:max-h-none
                   overflow-y-auto bg-surface-raised border border-line
                   rounded-t-2xl sm:rounded-none sm:border-y-0 sm:border-r-0
                   shadow-raised animate-fade-in outline-none
                   pb-[env(safe-area-inset-bottom)]"
      >
        {/* Grab handle on touch: signals the sheet can be dismissed. */}
        <div className="sm:hidden flex justify-center pt-3" aria-hidden>
          <div className="w-10 h-1 rounded-full bg-ink/15" />
        </div>

        <div className="sticky top-0 z-10 bg-surface-raised px-4 sm:px-5 py-4 border-b border-line
                        flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-start gap-2.5">
            <IconHelp className="w-5 h-5 text-brand shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 id={titleId} className="oc-h2">{hx('help.title')}</h2>
              <p className="oc-muted text-sm mt-0.5">{hx('help.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={hx('help.close')}
            className="p-2 -mr-2 rounded-lg text-ink-secondary hover:bg-surface-sunken shrink-0"
          >
            <svg viewBox="0 0 20 20" className="w-5 h-5" fill="currentColor" aria-hidden>
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-6">
          {isLoading && (
            <div className="py-8 grid place-items-center">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {isError && (
            <ErrorNotice
              message={hx('help.loadFailed')}
              onRetry={() => void refetch()}
              retryLabel={hx('help.retry')}
            />
          )}

          {data && <NextStepSection data={data} onNavigate={onClose} />}

          <HereSection route={route} manualSlug={manualSlug} onNavigate={onClose} />

          {data && <RoadmapSection data={data} onNavigate={onClose} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Section 1 — the single next best step (reused from onboarding)
// ---------------------------------------------------------------------------

function NextStepSection({
  data,
  onNavigate,
}: {
  data: NonNullable<ReturnType<typeof useOnboarding>['data']>;
  onNavigate: () => void;
}) {
  const { hx } = useHelpT();
  const { tx } = useOnboardingT();
  const { title, why, detail } = useStepText(data.steps);

  // Nobody has chosen a goal: the honest move is to ask, not to guess a plan
  // and present the guess as advice. Mirrors the dashboard card.
  if (data.askGoal) {
    return (
      <section>
        <SectionLabel>{hx('help.next.title')}</SectionLabel>
        <div className="rounded-xl border border-brand/30 bg-brand/[0.04] p-3.5 space-y-2">
          <p className="font-semibold text-ink">{hx('help.goal.title')}</p>
          <p className="oc-muted text-sm">{hx('help.goal.body')}</p>
          <Link to="/getting-started" onClick={onNavigate}>
            <Button variant="primary" size="sm">{hx('help.goal.choose')}</Button>
          </Link>
        </div>
      </section>
    );
  }

  if (data.complete) {
    return (
      <section>
        <SectionLabel>{hx('help.next.title')}</SectionLabel>
        <div className="rounded-xl border border-line p-3.5 space-y-1.5">
          <p className="font-semibold text-ink">{hx('help.complete.title')}</p>
          <p className="oc-muted text-sm">{hx('help.complete.body')}</p>
        </div>
        <div className="mt-3"><ProgressRow data={data} /></div>
      </section>
    );
  }

  const next = data.nextStep;

  return (
    <section>
      <SectionLabel>{hx('help.next.title')}</SectionLabel>
      {next ? (
        <div className="rounded-xl border border-brand/30 bg-brand/[0.04] p-3.5 space-y-2">
          <p className="text-base font-semibold text-ink text-balance">{title(next)}</p>
          <p className="oc-muted text-sm">{why(next)}</p>
          {detail(next) && (
            <p className="text-sm text-ink-secondary tabular-nums">{detail(next)}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Link to={next.href} onClick={onNavigate}>
              <Button variant="primary" size="sm">{tx('onboarding.open')}</Button>
            </Link>
            <Link to="/getting-started" onClick={onNavigate}>
              <Button variant="ghost" size="sm">{hx('help.next.viewAll')}</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-line p-3.5 space-y-2">
          <p className="oc-muted text-sm">{hx('help.next.allBlocked')}</p>
          <Link to="/getting-started" onClick={onNavigate}>
            <Button variant="secondary" size="sm">{hx('help.next.viewAll')}</Button>
          </Link>
        </div>
      )}
      <div className="mt-3"><ProgressRow data={data} /></div>
    </section>
  );
}

function ProgressRow({ data }: { data: NonNullable<ReturnType<typeof useOnboarding>['data']> }) {
  const { tx } = useOnboardingT();
  if (data.progress.total === 0) return null;
  const pct = Math.round((data.progress.done / data.progress.total) * 100);
  return (
    <div className="space-y-1.5">
      <ProgressBar value={pct} tone={data.complete ? 'good' : 'brand'} label={tx('onboarding.title')} />
      <p className="text-xs text-ink-muted tabular-nums">
        {tx('onboarding.progress', { done: data.progress.done, total: data.progress.total })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Section 2 — where you are (route-aware)
// ---------------------------------------------------------------------------

function HereSection({
  route,
  manualSlug,
  onNavigate,
}: {
  route: RouteHelp;
  manualSlug?: string;
  onNavigate: () => void;
}) {
  const { hx } = useHelpT();
  const { t } = useTranslation();
  const bullets = Array.from({ length: route.bullets }, (_, i) =>
    hx(`help.page.${route.page}.p${i + 1}`),
  );

  return (
    <section>
      <SectionLabel>{hx('help.here.title')}</SectionLabel>
      <p className="text-sm text-ink">{hx(`help.page.${route.page}.here`)}</p>

      {/* The deep link into the manual: the "?" for this screen opens the
          article that documents it, or the manual's contents if this screen
          has no dedicated article yet. */}
      <Link
        to={manualSlug ? `/manual/${manualSlug}` : '/manual'}
        onClick={onNavigate}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-brand/30
                   bg-brand/[0.04] px-3 py-2 text-sm font-medium text-brand hover:bg-brand/[0.08]"
      >
        <IconHelp className="w-4 h-4" aria-hidden />
        {hx(manualSlug ? 'help.manual.cta' : 'help.manual.ctaGeneric')}
      </Link>

      {bullets.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-ink-secondary">
              <span className="mt-2 w-1 h-1 rounded-full bg-ink-muted shrink-0" aria-hidden />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {route.links.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          {route.links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
            >
              {t(l.labelKey)}
              <IconChevron className="w-4 h-4" aria-hidden />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Section 3 — the mini roadmap (reused from onboarding)
// ---------------------------------------------------------------------------

function RoadmapSection({
  data,
  onNavigate,
}: {
  data: NonNullable<ReturnType<typeof useOnboarding>['data']>;
  onNavigate: () => void;
}) {
  const { hx } = useHelpT();
  const { tx } = useOnboardingT();
  const { title } = useStepText(data.steps);

  // The steps that come AFTER the one already shown above: everything not done,
  // minus the single next step, capped at three so it stays a glance.
  const upcoming = useMemo(() => {
    const outstanding = data.steps.filter((s) => s.state !== 'DONE');
    const afterNext = data.nextStep
      ? outstanding.filter((s) => s.key !== data.nextStep!.key)
      : outstanding;
    return afterNext.slice(0, 3);
  }, [data.steps, data.nextStep]);

  // Nothing to say once the goal is unanswered (we are still asking) or there
  // is no tail beyond the current step.
  if (data.askGoal || upcoming.length === 0) return null;

  return (
    <section>
      <SectionLabel>{hx('help.roadmap.title')}</SectionLabel>
      <ol className="space-y-2">
        {upcoming.map((step) => (
          <li key={step.key}>
            <Link
              to={step.href}
              onClick={onNavigate}
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 -mx-2.5
                         hover:bg-surface-sunken transition-colors group"
            >
              <span className="min-w-0 flex-1 text-sm text-ink truncate">{title(step)}</span>
              <Pill tone={STATE_TONE[step.state]}>{tx(`onboarding.state.${step.state}`)}</Pill>
              <IconChevron className="w-4 h-4 text-ink-muted shrink-0" aria-hidden />
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Shared
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold mb-2">
      {children}
    </p>
  );
}
